import { useEffect, useState } from "react";
import { Download, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useGet } from "../../lib/hooks";
import { useAuth } from "../../lib/auth";
import { Button } from "../ui/button";

const LV = [["Strongly Disagree", "#E53935"], ["Disagree", "#F6C46A"], ["Slightly Disagree", "#F3EFB0"], ["Neutral", "#F2E14A"], ["Slightly Agree", "#D9EBB0"], ["Agree", "#9CC98A"], ["Strongly Agree", "#6FBF3A"]];
const AGREE = ["Slightly Agree", "Agree", "Strongly Agree"];

function template() {
  const rows = [["Category", "Question", ...LV.map(([l]) => l)], ["Wellbeing", "I feel supported by my team leader", 0, 0, 0, 1, 2, 5, 4]];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" }));
  a.download = "staff-survey-template.csv"; a.click();
}

function Bar({ q }) {
  const total = LV.reduce((n, [l]) => n + (q.counts[l] || 0), 0) || 1;
  const agree = Math.round((AGREE.reduce((n, l) => n + (q.counts[l] || 0), 0) / total) * 100);
  return (
    <div className="py-2.5" data-testid="survey-question">
      <div className="mb-1.5 flex items-baseline gap-3 text-sm"><span className="flex-1">{q.question}</span><span className="whitespace-nowrap text-xs text-[#EBD6A0]">{agree}% agree</span></div>
      <div className="flex h-3 overflow-hidden rounded-full bg-white/5">
        {LV.map(([l, c]) => q.counts[l] ? <div key={l} style={{ width: `${(q.counts[l] / total) * 100}%`, background: c }} title={`${l}: ${q.counts[l]}`} /> : null)}
      </div>
    </div>
  );
}

export function StaffSurvey() {
  const { user } = useAuth();
  const [list, reload] = useGet("/records-survey");
  const [year, setYear] = useState(null);
  const [upYear, setUpYear] = useState(String(new Date().getFullYear()));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (list?.length && year === null) setYear(list[0].year); }, [list, year]);
  const cur = (list || []).find((x) => x.year === year);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("year", upYear); fd.append("file", file);
    setBusy(true);
    try { await api.post("/records-survey", fd); toast.success(`${upYear} results uploaded`); setYear(Number(upYear)); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const del = async () => { if (!window.confirm(`Delete the ${year} results?`)) return; try { await api.delete(`/records-survey/${year}`); setYear(null); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const cats = cur ? [...new Set(cur.questions.map((q) => q.category || ""))] : [];
  return (
    <div className="space-y-4" data-testid="staff-survey">
      <div className="flex flex-wrap items-center gap-2">
        {list?.length > 0 && <select value={year || ""} onChange={(e) => setYear(Number(e.target.value))} className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="survey-year">{list.map((x) => <option key={x.year} value={x.year}>{x.year} results</option>)}</select>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <input value={upYear} onChange={(e) => setUpYear(e.target.value.replace(/\D/g, "").slice(0, 4))} className="h-9 w-20 rounded-md border border-input bg-background px-3 text-sm" aria-label="Year" data-testid="survey-upload-year" />
          <label className={`inline-flex h-9 cursor-pointer items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground ${busy ? "opacity-60" : ""}`}><Upload className="mr-1 h-4 w-4" />{busy ? "Uploading…" : "Upload results"}
            <input type="file" accept=".csv,.xlsx" className="hidden" disabled={busy} onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="survey-upload-file" /></label>
          <Button size="sm" variant="outline" onClick={template} data-testid="survey-template"><Download className="mr-1 h-4 w-4" />Template</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 text-[11px]">{LV.map(([l, c]) => <span key={l} className="rounded px-2 py-0.5 font-medium text-[#141414]" style={{ background: c }}>{l}</span>)}</div>
      {!cur ? <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">No results yet. Upload a CSV or Excel file: one row per question with a column for each answer level (download the template), or one column per question with each person's answer. Future surveys built in BOS HQ will feed in here.</div> : (
        <div className="rounded-md border bg-card p-4" data-testid="survey-results">
          <div className="mb-2 flex items-center gap-3 text-xs text-muted-foreground"><span>{cur.questions.length} questions{cur.respondents ? ` · ${cur.respondents} responses` : ""} · from {cur.file_name}</span>
            {user.role !== "staff" && <button onClick={del} className="ml-auto inline-flex items-center gap-1 hover:text-red-400" data-testid="survey-delete"><Trash2 className="h-3.5 w-3.5" />Delete {year}</button>}</div>
          {cats.map((c) => <div key={c} className="divide-y divide-white/5">{c && <div className="mt-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#C2A462]">{c}</div>}
            {cur.questions.filter((q) => (q.category || "") === c).map((q, i) => <Bar key={i} q={q} />)}</div>)}
        </div>
      )}
    </div>
  );
}
